import { useTranslation } from "react-i18next";
import { TextButton } from "@/components/common/text-button";
import { cn } from "@/lib/utils";
import { CopyClipboard } from "@/components/common/copy-clipboard";
import { X } from "lucide-react";
import { Skeleton } from '@/components/ui/skeleton';
import { ReactNode, useMemo, useState } from "react";
import { AgentV2PushContentDetailData } from "@/services/agent-v2-push-content-detail";
import { formatDateSmart } from "@/utils/date-formatter";
import { Image } from '../common/image';
import { getModelNameByValue } from "../agent-creation/constants/ai-creation-model";
import { PreviewVideoImageFull } from "../common/preview-video-image-full";

interface PanelParams {
  name?: string;
  loading?: boolean;
  details?: AgentV2PushContentDetailData;
  onClose?: () => void;
}

export default function RightPanel({
  name,
  details,
  loading = false,
  onClose,
}: PanelParams) {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);

  const isAiCreation = details?.contentType === 6;

  const referenceImageUrls = Array.isArray(details?.fileUrl)
    ? details?.fileUrl as string[]
    : details?.fileUrl?.split(',').filter(Boolean) ?? [];

  const parameter = useMemo(() => {
    const dataArr = [
      getModelNameByValue(details?.model),
      details?.aspectRatio,
      details?.resolution,
      details?.duration ? `${details.duration}s` : null,
      details?.fileCount ? `${details.fileCount}张` : null,
    ];

    return dataArr.filter(Boolean).join('，');
  }, [details]);

  const typeMap = {
    '1': t('agent_dashboard.content_management.table.type_map.video'),
    '2': t('agent_dashboard.content_management.table.type_map.podcast'),
    '3': t('agent_dashboard.content_management.table.type_map.moments'),
    '4': t('agent_dashboard.content_management.table.type_map.image'),
    '5': t('agent_dashboard.content_management.table.type_map.article'),
  }

  // Ai创作内容类型
  const createSourceMap = {
    'AI-video': t('agent_dashboard.content_management.table.create_source_map.ai_video'),
    'AI-pic': t('agent_dashboard.content_management.table.create_source_map.ai_image'),
  }

  const newStatusMap = {
    '0': t('agent_dashboard.content_management.search.new_status.processing'),
    '1': t('agent_dashboard.content_management.search.new_status.finished'),
    '2': t('agent_dashboard.content_management.search.new_status.failed'),
    '9': t('agent_dashboard.content_management.search.new_status.deleted'),
    '10': t('agent_dashboard.content_management.search.new_status.timeout'),
  }

  const sourceMap = {
    '1': t('agent_dashboard.content_management.search.source_map.user'),
    '0': t('agent_dashboard.content_management.search.source_map.auto'),
  }

  return (
    <div className="min-w-[578px] h-full bg-i-white flex-1 flex flex-col gap-4">
      <div className="h-full flex flex-col flex-1 p-6 pb-0 overflow-hidden">
        <div className="flex justify-between items-center mb-3">
          <span className="text-xl font-semibold text-[rgba(16,24,40,1)]">
            {t("agent_dashboard.content_management.detail_modal.title")}
          </span>
          <button
            className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
            tabIndex={-1}
            onClick={() => {
              onClose?.();
            }}
          >
            <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
          </button>
        </div>
        {loading && <SkeletonRightPanel />}
        {!loading && (
          <div className="space-y-5 h-full overflow-y-auto">
            <div>
              <div className="text-base font-semibold text-[rgba(16,24,40,1)] mb-4">
                {t("agent_dashboard.content_management.detail_modal.content_info.title")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.content_type")}
                  value={
                    <div className={cn(
                      "text-sm font-medium py-1 px-2 rounded-full max-w-max",
                      "text-[rgba(6,118,118,1)] bg-[rgba(238,254,255,1)]",
                      "text-[rgba(89,37,220,1)] bg-[rgba(244,243,255,1)]",
                      "text-[rgba(181,71,8,1)] bg-[rgba(255,250,235,1)]",
                      "text-[rgba(193,21,116,1)] bg-[rgba(253,242,250,1)]",
                      "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                    )}>
                      {[1, 2, 3, 4, 5].includes(Number(details?.contentType)) && typeMap[details?.contentType?.toString() || '']}
                      {[6].includes(Number(details?.contentType)) && createSourceMap[details?.createSource?.toString() || '']}
                    </div>
                  }
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.push_status")}
                  value={
                    <>
                      <div className={cn(
                        "text-sm font-medium py-1 px-2 rounded-full max-w-max",
                        "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                        details?.status === 0 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                        details?.status === 9 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                        details?.status === 1 && "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                        [2, 10].includes(details?.status || 0) && "text-[rgba(192,16,72,1)] bg-[rgba(255,241,243,1)]",
                      )}>
                        {newStatusMap[details?.status?.toString() || ''] || '-'}
                      </div>
                      {/* <Tooltip
                        side='top'
                        align='center'
                        trigger={
                          <div className={cn(
                            "text-base font-medium py-1 px-2 rounded-full max-w-max",
                            "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                            details?.status === 0 && "text-[rgba(23,92,211,1)] bg-[rgba(239,248,255,1)]",
                            details?.status === 9 && "text-[rgba(52,64,84,1)] bg-[rgba(242,244,247,1)]",
                            details?.status === 1 && "text-[rgba(6,118,71,1)] bg-[rgba(236,253,243,1)]",
                            [2, 10].includes(details?.status || 0) && "text-[rgba(192,16,72,1)] bg-[rgba(255,241,243,1)]",
                          )}>
                            {newStatusMap[details?.status?.toString() || ''] || '-'}
                          </div>
                        }
                        content={
                          <div className='relative bg-[rgba(16,24,40,1)] px-3 py-2  rounded-[8px]'>
                            <span className='text-[rgba(255,255,255,1)] text-sm font-medium'>
                              warning
                            </span>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-black"></div>
                          </div>
                        }
                      /> */}
                    </>
                  }
                />
                <Description
                  className="col-span-2"
                  label={t("agent_dashboard.content_management.detail_modal.content_info.user_instruction")}
                  value={details?.inputText || '-'}
                />
                <Description
                  className="col-span-2"
                  label={t("agent_dashboard.content_management.detail_modal.content_info.content_title")}
                  value={details?.contentTitle || details?.content}
                />
                {isAiCreation && (
                  <Description
                    label={t("agent_dashboard.content_management.detail_modal.content_info.reference_image")}
                    className={referenceImageUrls?.length > 4 ? 'col-span-2' : ''}
                    children={
                      <div className="flex flex-wrap gap-1.5">
                        {referenceImageUrls?.length > 0 ? referenceImageUrls?.map((url, index) => (
                          <Image
                            key={index}
                            src={url}
                            className="w-[52px] h-[70px] object-cover rounded-[6px] flex-shrink-0 cursor-pointer"
                            onClick={() => {
                              setShowPreview(true);
                              setCurrentIndex(index);
                            }}
                          />
                        )) : '-'}
                      </div>
                    }
                  />
                )}
                {isAiCreation && (
                  <Description
                    label={t("agent_dashboard.content_management.detail_modal.content_info.model_detail")}
                    value={parameter}
                  />
                )}
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.content_source")}
                  value={sourceMap[details?.contentResource?.toString() || ''] || '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.template_id")}
                  value={
                    <span className="break-all text-sm">
                      {details?.taskId || '-'}
                      {details?.taskId && <CopyClipboard
                        text={details?.taskId}
                        className='inline-flex align-bottom ml-2'
                      />}
                    </span>
                  }
                />
                {/* <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.input_way")}
                  value={'文字'}
                /> */}
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.creation_time")}
                  value={details?.createdAt ? formatDateSmart(details?.createdAt * 1000) : '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.pushed_time")}
                  value={details?.pushAt ? formatDateSmart(details?.pushAt * 1000) : '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.content_info.view_time")}
                  value={details?.visitAt ? formatDateSmart(details?.visitAt * 1000) : '-'}
                />
              </div>
            </div>
            <div>
              <div className="text-base font-semibold text-[rgba(16,24,40,1)] mb-4">
                {t("agent_dashboard.content_management.detail_modal.user_info.title")}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.user_info.user_name")}
                  value={details?.userName || '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.user_info.user_id")}
                  value={details?.userId || '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.user_info.user_email")}
                  value={details?.userEmail || '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.user_info.created_count")}
                  value={details?.produceCount?.toString() || '-'}
                />
                <Description
                  label={t("agent_dashboard.content_management.detail_modal.user_info.pushed_count")}
                  value={details?.pushCount?.toString() || '-'}
                />
              </div>
            </div>
          </div>
        )}
      </div>
      {/* 操作按钮 */}
      <div className="w-full flex p-6 justify-end gap-3 border-t border-[rgba(233,234,235,1)]">
        <TextButton
          label={t("agent_dashboard.content_management.detail_modal.close")}
          className='h-10 w-[147px] bg-white border border-[rgba(213,215,218,1)] hover:bg-gray-50 font-medium text-sm !text-[rgba(52,64,84,1)]'
          onClick={() => {
            onClose?.();
          }}
        />
      </div>
      {showPreview && (
        <PreviewVideoImageFull
          open={showPreview}
          previewList={referenceImageUrls?.map((item) => ({
            url: item,
            type: "image",
          }))}
          currentIndex={currentIndex}
          onOpenChange={setShowPreview}
        />
      )}
    </div>
  );
}

const Description = ({
  label,
  value,
  className,
  children,
}: {
  label: ReactNode;
  value?: ReactNode;
  className?: string;
  children?: ReactNode;
}) => {
  return (
    <div className={cn("flex flex-col", className)}>
      <div className="text-sm text-[rgba(102,112,133,1)] pb-2">
        {label}
      </div>
      {children || <div className="text-sm text-[rgba(16,24,40,1)] break-all">{value || '-'}</div>}
    </div>
  );
}

// 骨架屏组件
export const SkeletonRightPanel = () => {
  return (
    <div className="space-y-5 h-full overflow-y-auto">
      <div>
        <div className="text-base font-semibold text-[rgba(16,24,40,1)] mb-4">
          <Skeleton className="h-5 w-32" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-7 w-20 rounded-full" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-7 w-20 rounded-full" />
          </div>
          <div className="flex flex-col col-span-2">
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex flex-col col-span-2">
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="size-4" />
            </div>
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </div>
      <div>
        <div className="text-base font-semibold text-[rgba(16,24,40,1)] mb-4">
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-32" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-8" />
          </div>
          <div className="flex flex-col">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-4 w-12" />
          </div>
        </div>
      </div>
    </div>
  );
}
